/**
 * 应答配置 › 标签字典（`#tags`，专题《标签体系 v2》批 1 ／ 批 2）：一本标签字典，分维度、最多两级；每个维度标明来源
 * （平台内置 ／ 系统计算 ／ 客户自定义）、出处、判定方式、取值从哪读。两个页签：
 * - 字典：左侧维度列表（来源徽标；留位的维度灰显「未上线」），右侧该维度的标签——两级的按一级分组，点一行开抽屉
 *   看四段说明、编号、来源、出处、判定方式、被引用（可跳工具能力 ／ 应答指南 ／ 政策知识页）；
 * - 体检：同义 ／ 重叠、只有一个叶子的一级、没人引用、引用了不存在或停用的标签、说明缺「不包含」或例句不足（只出报告）。
 *   「没人引用」只查意图、业务对象：系统计算类（数据状态、会话状态）由代码算、给 Jev ／ N2 看，没人引用是正常的（收尾 B5）。
 * 全部只读（`GET /tags`），本期由平台维护，改动走版本。查询参数 `tag`：标签编号 → 打开那一条；维度编号（如 `slot`）→ 切到那个维度
 * （信号识别页跳过来）。批 2：业务对象只留 4 类、原实体并进信息项（标「原为实体」）、数据状态带说明与计算口径、情绪与风险上线。
 * 显示名：意图、一级、店铺、业务对象、情绪与风险取后端；数据状态、会话状态后端只有键，按召回页中文表（recall-labels）显示。
 * 批 5：内容分类（知识类型）上线，被引用跳政策知识页；准入的上线状态显示名取后端（tags/admission 一处口径），体检写明准入怎么查；
 * 页头写字典版本（配置快照保存时记下同一个版本）。
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import { Tabs } from "@/components/ui/tabs";
import {
	type PlanTag,
	type PlanTagDictionary,
	type PlanTagDimension,
	type PlanTagHealth,
	type PlanTagRef,
	planErrorMessage,
	usePlanTags,
} from "@/lib/api/plan-lab";
import { cn } from "@/lib/utils";

import { FailState } from "../kit/fail-state";
import { CellTags, type PlanColumn, PlanTable } from "../kit/plan-table";
import { ENTITY_LABEL, STATE_LABEL } from "../recall/recall-labels";

type TabKey = "dict" | "health";

const SOURCE_ZH: Record<string, string> = {
	platform: "平台内置",
	computed: "系统计算",
	custom: "客户自定义",
};
const SOURCE_TONE: Record<string, BadgeTone> = {
	platform: "info",
	computed: "neutral",
	custom: "success",
};
const ORIGIN_ZH: Record<string, string> = { template: "行业模板", customer: "客户新增" };
const JUDGE_ZH: Record<string, string> = {
	model: "模型",
	code: "代码",
	model_code: "模型 ＋ 代码",
};
const STATE_ZH: Record<string, { label: string; tone: BadgeTone }> = {
	published: { label: "已发布", tone: "neutral" },
	never_produced: { label: "从不产出", tone: "warning" },
};
const REF_KIND: Record<PlanTagRef["kind"], { label: string; hash: string }> = {
	tool: { label: "工具", hash: "tools" },
	guide: { label: "指南", hash: "guidance" },
	knowledge: { label: "知识", hash: "knowledge" },
	// 层体检（缺层 ／ 越层 ／ 政策句 ／ 重复候选）涉及的对象
	rule: { label: "规则", hash: "guards" },
	intent: { label: "意图", hash: "tags" },
	dimension: { label: "标签维度", hash: "tags" },
};
const VIA_ZH: Record<string, string> = {
	intents: "意图",
	intent_tags: "意图标签（加分）",
	entities: "业务对象",
	slots: "信息项",
	data_states: "适用状态",
	preconditions: "执行前置",
	entity_conds: "对象条件",
	slot_conds: "信息项条件",
	data_conds: "数据条件",
	dialogue_conds: "会话条件",
	"signal_tags.sentiment": "情绪标签（加分）",
	"signal_tags.risk_flags": "风险旗标签（加分）",
	"constraint.unless": "约束例外",
	stores: "店铺",
	status: "上线状态",
	kind: "知识类型",
};
const ALL = "__all__";

/** 标签显示名：后端给了用后端的；业务对象、数据状态、会话状态按召回页中文表；都没有原样显示键 */
function tagName(dim: string, t: PlanTag): string {
	if (t.name) return t.name;
	if (dim === "object") return ENTITY_LABEL[t.key] ?? t.key;
	if (dim === "data_state" || dim === "dialogue_state") return STATE_LABEL[t.key] ?? t.key;
	return t.key;
}

function isLeaf(d: PlanTagDimension, t: PlanTag): boolean {
	return t.level === 2 || d.levels === 1;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-xs text-text-muted">{label}</div>
			<div className="whitespace-pre-wrap break-words text-sm text-text-primary">{children}</div>
		</div>
	);
}

function SourceBadge({ source }: { source: string }) {
	return <Badge tone={SOURCE_TONE[source] ?? "muted"}>{SOURCE_ZH[source] ?? source}</Badge>;
}

export function TagsTab({ focusCode }: { focusCode?: string } = {}) {
	const [tab, setTab] = useState<TabKey>("dict");
	const q = usePlanTags();
	const data = q.data;
	const tabs = [
		{ key: "dict" as const, label: "字典" },
		{ key: "health" as const, label: `体检${data ? `（${data.health.length}）` : ""}` },
	];
	return (
		<div className="space-y-3" data-testid="plan-block-tags">
			<Tabs items={tabs} active={tab} onChange={setTab} />
			{q.isLoading ? (
				<Card>
					<CardContent className="py-4">
						<LoadingInline />
					</CardContent>
				</Card>
			) : q.isError || !data ? (
				<Card>
					<CardContent>
						<FailState
							reason={planErrorMessage(q.error)}
							onRetry={() => q.refetch()}
							retrying={q.isFetching}
							testId="plan-tags-fail"
						/>
					</CardContent>
				</Card>
			) : tab === "dict" ? (
				<DictPanel data={data} focusCode={focusCode} />
			) : (
				<HealthPanel data={data} />
			)}
		</div>
	);
}

/* ── 字典 ─────────────────────────────────────────────── */

function DictPanel({ data, focusCode }: { data: PlanTagDictionary; focusCode?: string }) {
	const navigate = useNavigate();
	const [source, setSource] = useState(ALL);
	const [dimId, setDimId] = useState(data.dimensions[0]?.id ?? "intent");
	const [open, setOpen] = useState<{ dim: string; tag: PlanTag } | null>(null);
	const dims = data.dimensions.filter((d) => source === ALL || d.source === source);
	const dim = data.dimensions.find((d) => d.id === dimId) ?? data.dimensions[0];

	useEffect(() => {
		if (!focusCode) return;
		if (data.dimensions.some((d) => d.id === focusCode)) {
			setSource(ALL);
			setDimId(focusCode);
			return;
		}
		for (const [d, tags] of Object.entries(data.tags)) {
			const hit = tags.find((t) => t.code === focusCode);
			if (hit) {
				setSource(ALL);
				setDimId(d);
				setOpen({ dim: d, tag: hit });
				return;
			}
		}
	}, [focusCode, data]);
	const close = () => {
		setOpen(null);
		if (focusCode) navigate({ to: "/plan-lab", search: {}, hash: "tags", replace: true });
	};
	// 维度名在窄宽度下不截断：左栏给够宽度，名字可换行（批 2 顺手修「情绪…」「内容…」）

	return (
		<Card>
			<CardContent className="space-y-3 py-4" data-testid="plan-tags-dict">
				<div className="flex flex-wrap items-center gap-3">
					<LabeledSelect
						label="来源"
						value={source}
						onChange={(v) => {
							setSource(v);
							const first = data.dimensions.find((d) => v === ALL || d.source === v);
							if (first && v !== ALL && dim?.source !== v) setDimId(first.id);
						}}
						options={[
							{ value: ALL, label: "全部" },
							...Object.entries(SOURCE_ZH).map(([value, label]) => ({ value, label })),
						]}
					/>
					<span className="text-xs text-text-muted" data-testid="plan-tags-readonly">
						本期由平台维护，改动走版本：平台内置与系统计算的维度不让客户改（含显示名与说明）；意图表{" "}
						<span className="font-mono">{data.intent_table}</span> · 画像{" "}
						<span className="font-mono">{data.profile_version}</span>
						{data.dict_version ? (
							<>
								{" "}
								· 字典版本{" "}
								<span className="font-mono" data-testid="plan-tags-dict-version">
									{data.dict_version}
								</span>
							</>
						) : null}
					</span>
				</div>
				<div className="flex flex-col gap-4 lg:flex-row">
					<nav
						className="flex shrink-0 flex-col gap-1 lg:w-64"
						data-testid="plan-tags-dims"
						aria-label="维度"
					>
						{dims.map((d) => {
							const on = d.id === dim?.id;
							const reserved = d.status === "reserved";
							return (
								<button
									key={d.id}
									type="button"
									aria-pressed={on}
									data-testid="plan-tags-dim"
									data-dim={d.id}
									onClick={() => setDimId(d.id)}
									className={cn(
										"flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors duration-150",
										on
											? "border-brand-accent bg-brand-accent/5 text-text-primary"
											: "border-surface-border text-text-secondary hover:bg-surface-subtle",
										reserved && "opacity-60",
									)}
								>
									<span className="min-w-0 break-words" title={d.name}>
										{d.name}
									</span>
									<span className="flex shrink-0 items-center gap-1">
										{reserved ? (
											<span className="text-xs text-text-muted">未上线</span>
										) : (
											<span className="text-xs text-text-muted">{d.count}</span>
										)}
										<SourceBadge source={d.source} />
									</span>
								</button>
							);
						})}
					</nav>
					{dim ? (
						<DimView data={data} dim={dim} onOpen={(t) => setOpen({ dim: dim.id, tag: t })} />
					) : null}
				</div>
				<div className="text-xs text-text-muted" data-testid="plan-tags-count">
					共 {data.dimensions.length} 个维度，留位{" "}
					{data.dimensions.filter((d) => d.status === "reserved").length} 个；标签{" "}
					{data.dimensions.reduce((n, d) => n + d.count, 0)} 个
				</div>
			</CardContent>
			<TagDrawer data={data} open={open} onClose={close} />
		</Card>
	);
}

function DimView({
	data,
	dim,
	onOpen,
}: {
	data: PlanTagDictionary;
	dim: PlanTagDimension;
	onOpen: (t: PlanTag) => void;
}) {
	const tags = data.tags[dim.id] ?? [];
	const groups = tags.filter((t) => t.level === 1);
	const leaves = tags.filter((t) => isLeaf(dim, t));
	// 各组表格列宽一致（固定宽），在 1440 宽的右栏里不横滚；状态只在不是「已发布」时跟在显示名后面
	const cols: PlanColumn<PlanTag>[] = [
		{
			key: "name",
			title: "显示名",
			className: "w-40 max-w-[160px]",
			render: (t) => {
				const s = t.state === "published" ? null : STATE_ZH[t.state];
				return (
					<span className="flex min-w-0 items-center gap-1.5">
						<span className="truncate text-text-primary" title={tagName(dim.id, t)}>
							{tagName(dim.id, t)}
						</span>
						{s ? <Badge tone={s.tone}>{s.label}</Badge> : null}
						{t.from_entity ? (
							<span title={`原为实体 ${t.from_entity}，批 2 并进信息项`}>
								<Badge tone="muted">原为实体</Badge>
							</span>
						) : null}
					</span>
				);
			},
		},
		{
			key: "code",
			title: "编号",
			className: "w-60 max-w-[240px]",
			render: (t) => (
				<span className="block truncate font-mono text-xs text-text-secondary" title={t.code}>
					{t.code}
				</span>
			),
		},
		{
			key: "desc",
			title: "说明",
			className: "w-[264px] max-w-[264px]",
			render: (t) => {
				const text = t.explain?.definition || t.desc;
				return text ? (
					<span className="block truncate text-text-secondary" title={text}>
						{text}
					</span>
				) : (
					<span className="text-text-muted">—</span>
				);
			},
		},
		{
			key: "refs",
			title: "被引用",
			className: "w-16",
			align: "right",
			render: (t) =>
				t.refs.length ? (
					t.refs.length
				) : (
					<span className="text-text-muted" title="没有工具、生效指南或上线知识引用">
						0
					</span>
				),
		},
	];
	return (
		<div className="min-w-0 flex-1 space-y-3" data-testid="plan-tags-dim-view" data-dim={dim.id}>
			<div className="space-y-2 rounded-lg border border-surface-border px-4 py-3">
				<div className="flex flex-wrap items-center gap-2">
					<h3 className="text-sm font-semibold text-text-primary">{dim.name}</h3>
					<span className="text-xs text-text-muted">{dim.question}</span>
					<SourceBadge source={dim.source} />
					{dim.status === "reserved" ? <Badge tone="muted">未上线</Badge> : null}
				</div>
				<div className="grid gap-x-6 gap-y-1 text-xs text-text-secondary md:grid-cols-2">
					<div>层级：{dim.levels === 2 ? "两级（一级 → 二级，只在二级打标）" : "一级"}</div>
					<div>
						判定方式：{JUDGE_ZH[dim.judge] ?? dim.judge}
						{dim.origin ? ` · 出处：${ORIGIN_ZH[dim.origin] ?? dim.origin}` : ""}
					</div>
					<div className="md:col-span-2">取值从哪读：{dim.values_from}</div>
					<div className="md:col-span-2">用在哪：{dim.used_for}</div>
				</div>
			</div>
			{dim.status === "reserved" || !tags.length ? (
				<EmptyState title="未上线" desc={dim.values_from} className="py-8" />
			) : dim.levels === 2 && groups.length ? (
				<div className="space-y-3" data-testid="plan-tags-tree">
					{groups.map((g) => {
						const kids = leaves.filter((t) => t.parent === g.code);
						return (
							<section key={g.code} className="space-y-2" data-testid="plan-tags-group">
								<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
									<h4 className="text-sm font-medium text-text-primary">{tagName(dim.id, g)}</h4>
									<span className="font-mono text-xs text-text-muted">{g.code}</span>
									{g.desc ? <span className="text-xs text-text-muted">{g.desc}</span> : null}
									<span className="text-xs text-text-muted">{kids.length} 个</span>
								</div>
								<PlanTable
									columns={cols}
									rows={kids}
									rowKey={(t) => t.code}
									onRowClick={onOpen}
									density="compact"
									rowTestId="plan-tags-row"
								/>
							</section>
						);
					})}
				</div>
			) : (
				<PlanTable
					columns={cols}
					rows={leaves}
					rowKey={(t) => t.code}
					onRowClick={onOpen}
					density="compact"
					rowTestId="plan-tags-row"
				/>
			)}
			{dim.id === "intent" && data.extra_keys.length ? (
				<div className="space-y-1 text-xs text-text-muted" data-testid="plan-tags-extra">
					<div>意图表外的键（只用于显示兜底，不是意图）：</div>
					{data.extra_keys.map((x) => (
						<div key={x.key}>
							<span className="font-mono">{x.key}</span> → {x.name}（{x.kind}）：{x.note}
						</div>
					))}
				</div>
			) : null}
		</div>
	);
}

function RefList({ refs }: { refs: PlanTagRef[] }) {
	if (!refs.length)
		return <span className="text-text-muted">无（没有工具、生效指南或上线知识引用）</span>;
	const kinds = (Object.keys(REF_KIND) as PlanTagRef["kind"][]).filter((k) =>
		refs.some((r) => r.kind === k),
	);
	return (
		<div className="space-y-2" data-testid="plan-tags-refs">
			{kinds.map((k) => (
				<div key={k} className="flex flex-wrap items-center gap-1.5">
					<span className="w-10 shrink-0 text-xs text-text-muted">{REF_KIND[k].label}</span>
					{refs
						.filter((r) => r.kind === k)
						.map((r) => (
							<Link
								key={`${r.id}-${r.via}`}
								to="/plan-lab"
								search={{}}
								hash={REF_KIND[k].hash}
								title={`去${REF_KIND[k].label}页（经 ${VIA_ZH[r.via] ?? r.via}）`}
								className="rounded-full border border-surface-border px-2 py-0.5 font-mono text-xs text-text-secondary hover:border-brand-accent hover:text-brand-accent"
								data-testid="plan-tags-ref"
							>
								{r.id}
								<span className="ml-1 font-sans text-text-muted">{VIA_ZH[r.via] ?? r.via}</span>
							</Link>
						))}
				</div>
			))}
		</div>
	);
}

function TagDrawer({
	data,
	open,
	onClose,
}: {
	data: PlanTagDictionary;
	open: { dim: string; tag: PlanTag } | null;
	onClose: () => void;
}) {
	const dim = open ? data.dimensions.find((d) => d.id === open.dim) : undefined;
	const t = open?.tag;
	const parent =
		t?.parent && open ? data.tags[open.dim]?.find((x) => x.code === t.parent) : undefined;
	const st = t ? (STATE_ZH[t.state] ?? { label: t.state, tone: "muted" as BadgeTone }) : null;
	return (
		<Drawer
			open={!!open}
			onClose={onClose}
			width="min(640px,95vw)"
			testId="plan-tags-drawer"
			title={t && open ? <span>{tagName(open.dim, t)}</span> : ""}
			description={t ? <span className="font-mono">{t.code}</span> : null}
			badges={
				dim && t ? (
					<>
						<SourceBadge source={dim.source} />
						{st && t.state !== "published" ? <Badge tone={st.tone}>{st.label}</Badge> : null}
					</>
				) : null
			}
		>
			{t && dim && open ? (
				<>
					{t.explain ? (
						<DrawerSection title="说明" testId="plan-tags-explain">
							<Field label="定义（进信号模型）">{t.explain.definition}</Field>
							<Field label="包含（进信号模型）">{t.explain.includes}</Field>
							<Field label="不包含（进信号模型；和谁易混、怎么分）">
								{t.explain.excludes || "—"}
							</Field>
							<Field label={`例句（${t.explain.examples.length}，只在后台显示）`}>
								<ul className="space-y-1">
									{t.explain.examples.map((e) => (
										<li key={`${e.from ?? ""}-${e.text}`}>
											「{e.text}」
											{e.from ? (
												<span className="ml-1 font-mono text-xs text-text-muted">{e.from}</span>
											) : null}
										</li>
									))}
								</ul>
							</Field>
						</DrawerSection>
					) : t.desc ? (
						<DrawerSection title="说明">
							<div className="whitespace-pre-wrap text-sm text-text-primary">{t.desc}</div>
						</DrawerSection>
					) : null}
					<DrawerSection title="登记">
						<div className="grid gap-3 sm:grid-cols-2">
							<Field label="维度">{dim.name}</Field>
							<Field label="键">
								<span className="font-mono text-xs">{t.key}</span>
							</Field>
							<Field label="上级">{parent ? tagName(open.dim, parent) : "—"}</Field>
							<Field label="层级">{t.level === 1 ? "一级" : "二级"}</Field>
							<Field label="来源">{SOURCE_ZH[dim.source] ?? dim.source}</Field>
							<Field label="出处">{dim.origin ? (ORIGIN_ZH[dim.origin] ?? dim.origin) : "—"}</Field>
							<Field label="判定方式">{JUDGE_ZH[dim.judge] ?? dim.judge}</Field>
							<Field label="取值从哪读">{dim.values_from}</Field>
						</div>
						{t.how ? (
							<div data-testid="plan-tags-how">
								<Field label="计算口径">{t.how}</Field>
							</div>
						) : null}
						{t.fields?.length ? <Field label="来自字段">{t.fields.join("、")}</Field> : null}
						{t.notes.length ? <Field label="备注">{t.notes.join("；")}</Field> : null}
						{t.rules?.length ? <Field label="计算规则">{t.rules.join("、")}</Field> : null}
					</DrawerSection>
					<DrawerSection title={`被引用（${t.refs.length}）`}>
						<RefList refs={t.refs} />
					</DrawerSection>
				</>
			) : null}
		</Drawer>
	);
}

/* ── 体检 ─────────────────────────────────────────────── */

function HealthPanel({ data }: { data: PlanTagDictionary }) {
	const [kind, setKind] = useState(ALL);
	const dimName = useMemo(
		() => Object.fromEntries(data.dimensions.map((d) => [d.id, d.name])),
		[data],
	);
	const rows = data.health.filter((h) => kind === ALL || h.kind === kind);
	const cols: PlanColumn<PlanTagHealth>[] = [
		{
			key: "kind",
			title: "类别",
			className: "w-40",
			render: (h) => <Badge tone="warning">{data.check_kinds[h.kind] ?? h.kind}</Badge>,
		},
		{
			key: "dim",
			title: "维度",
			className: "w-20",
			render: (h) => dimName[h.dim] ?? h.dim,
		},
		{
			key: "keys",
			title: "标签",
			className: "max-w-[160px]",
			render: (h) => (h.keys.length ? <CellTags mono max={2} items={h.keys} /> : "—"),
		},
		{
			key: "msg",
			title: "说明",
			className: "max-w-[360px]",
			render: (h) => (
				<span className="block truncate text-text-secondary" title={h.msg}>
					{h.msg}
				</span>
			),
		},
		{
			key: "refs",
			title: "涉及对象",
			className: "max-w-[180px]",
			render: (h) =>
				h.refs.length ? (
					<CellTags mono max={1} items={h.refs.map((r) => r.id)} />
				) : (
					<span className="text-text-muted">—</span>
				),
		},
	];
	return (
		<Card>
			<CardContent className="space-y-3 py-4" data-testid="plan-tags-health">
				<div className="flex flex-wrap items-center gap-3">
					<LabeledSelect
						label="类别"
						value={kind}
						onChange={setKind}
						options={[
							{ value: ALL, label: "全部" },
							...Object.entries(data.check_kinds).map(([value, label]) => ({ value, label })),
						]}
					/>
					<span className="text-xs text-text-muted">
						发布前自动跑的字典体检：只出报告，不阻断运行；处理去向见专题《标签体系 v2》分批计划。
					</span>
				</div>
				{data.health_scope?.unreferenced || data.health_scope?.admission ? (
					<div
						className="space-y-1 rounded-md bg-surface-subtle px-3 py-2 text-xs text-text-secondary"
						data-testid="plan-tags-health-scope"
					>
						{data.health_scope.unreferenced ? <div>{data.health_scope.unreferenced}</div> : null}
						{data.health_scope.admission ? (
							<div data-testid="plan-tags-health-admission">{data.health_scope.admission}</div>
						) : null}
					</div>
				) : null}
				{rows.length ? (
					<PlanTable
						columns={cols}
						rows={rows}
						rowKey={(h) => `${h.kind}-${h.dim}-${h.keys.join(",")}-${h.msg}`}
						density="compact"
						rowTestId="plan-tags-health-row"
					/>
				) : (
					<EmptyState title="这一类没有发现问题" className="py-8" />
				)}
				<div className="text-xs text-text-muted" data-testid="plan-tags-health-count">
					共 {rows.length} 条
				</div>
			</CardContent>
		</Card>
	);
}
